import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ProductCatalogMultiPicker } from "@/components/ProductCatalogMultiPicker";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import {
  MeetingAuthenticationTypeEnum,
  resolveAllowedAuthenticationTypes,
} from "@/types/MeetingAuthenticationType";
import { productHasPrice } from "@/lib/productHasPrice";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";

const AUTHENTICATION_TYPE_OPTIONS = [
  { value: MeetingAuthenticationTypeEnum.EMAIL, label: "Email" },
  { value: MeetingAuthenticationTypeEnum.SMS, label: "SMS" },
] as const;

function resolveRequireAuthentication(product: ProductEditPageProduct): boolean {
  if (productHasPrice(product)) {
    return true;
  }
  return product.meeting?.requireAuthentication ?? false;
}

function resolveAccessProductIds(product: ProductEditPageProduct): string[] {
  return (product.access?.productIds ?? [])
    .map((id) => String(id).trim())
    .filter(Boolean);
}

export function ProductEditAccessTab({
  productId,
  product,
}: {
  productId: string;
  product: ProductEditPageProduct;
}) {
  const hasPrice = productHasPrice(product);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAuth, setIsSavingAuth] = useState(false);
  const [isSavingAuthTypes, setIsSavingAuthTypes] = useState(false);
  const [isSavingAuthMessage, setIsSavingAuthMessage] = useState(false);
  const [requireAuthentication, setRequireAuthentication] = useState(() =>
    resolveRequireAuthentication(product),
  );
  const [allowedAuthenticationTypes, setAllowedAuthenticationTypes] = useState(() =>
    resolveAllowedAuthenticationTypes(product.meeting?.allowedAuthenticationTypes),
  );
  const [authenticationMessage, setAuthenticationMessage] = useState(
    () => product.meeting?.authenticationMessage?.trim() ?? "",
  );
  const [accessProductIds, setAccessProductIds] = useState(() =>
    resolveAccessProductIds(product),
  );
  const [catalogProducts, setCatalogProducts] = useState<ProductCatalogOption[]>([]);

  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then((data) => {
        const loaded = (data.products as ProductCatalogOption[] | undefined) ?? [];
        setCatalogProducts(loaded);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setAccessProductIds(resolveAccessProductIds(product));
  }, [product.access?.productIds]);

  useEffect(() => {
    setRequireAuthentication(resolveRequireAuthentication(product));
    setAllowedAuthenticationTypes(
      resolveAllowedAuthenticationTypes(product.meeting?.allowedAuthenticationTypes),
    );
    setAuthenticationMessage(product.meeting?.authenticationMessage?.trim() ?? "");
  }, [
    product.meeting?.requireAuthentication,
    product.meeting?.allowedAuthenticationTypes,
    product.meeting?.authenticationMessage,
    product.price,
    product.variants,
  ]);

  const handleAccessChange = async (next: string[]) => {
    const previous = accessProductIds;
    setAccessProductIds(next);
    setIsSaving(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access: { productIds: next } }),
      });
      const data = await response.json();
      if (!response.ok) {
        setAccessProductIds(previous);
        alert(data.error || "Failed to update access products");
      }
    } catch (error) {
      console.error("Failed to update access products:", error);
      setAccessProductIds(previous);
      alert("An error occurred while saving");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequireAuthenticationChange = async (checked: boolean) => {
    if (hasPrice) return;

    setRequireAuthentication(checked);
    setIsSavingAuth(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meeting: { requireAuthentication: checked } }),
      });
      const data = await response.json();
      if (!response.ok) {
        setRequireAuthentication(!checked);
        alert(data.error || "Failed to update authentication setting");
      }
    } catch (error) {
      console.error("Failed to update requireAuthentication:", error);
      setRequireAuthentication(!checked);
      alert("An error occurred while saving");
    } finally {
      setIsSavingAuth(false);
    }
  };

  const handleAuthenticationMessageSave = async (value: string) => {
    const trimmed = value.trim();
    const previous = product.meeting?.authenticationMessage?.trim() ?? "";
    if (trimmed === previous) return;

    setAuthenticationMessage(trimmed);
    setIsSavingAuthMessage(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting: { authenticationMessage: trimmed || null },
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setAuthenticationMessage(previous);
        alert(data.error || "Failed to update authentication message");
      }
    } catch (error) {
      console.error("Failed to update authenticationMessage:", error);
      setAuthenticationMessage(previous);
      alert("An error occurred while saving");
    } finally {
      setIsSavingAuthMessage(false);
    }
  };

  const handleAllowedAuthenticationTypesChange = async (
    type: MeetingAuthenticationTypeEnum,
    checked: boolean,
  ) => {
    const previous = allowedAuthenticationTypes;
    const next = checked
      ? [...new Set([...allowedAuthenticationTypes, type])]
      : allowedAuthenticationTypes.filter((value) => value !== type);

    if (next.length === 0) {
      alert("At least one authentication type is required");
      return;
    }

    setAllowedAuthenticationTypes(next);
    setIsSavingAuthTypes(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meeting: { allowedAuthenticationTypes: next } }),
      });
      const data = await response.json();
      if (!response.ok) {
        setAllowedAuthenticationTypes(previous);
        alert(data.error || "Failed to update authentication types");
      }
    } catch (error) {
      console.error("Failed to update allowedAuthenticationTypes:", error);
      setAllowedAuthenticationTypes(previous);
      alert("An error occurred while saving");
    } finally {
      setIsSavingAuthTypes(false);
    }
  };

  const showAuthenticationTypes = requireAuthentication || hasPrice;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
        <div className="space-y-1">
          <Label htmlFor="requireAuthentication">Require authentication to join</Label>
          <p className="text-sm text-muted-foreground">
            {hasPrice
              ? "Required for paid meetings. Viewers must verify the email or phone used at checkout before joining."
              : "Viewers must verify their email or phone before joining."}
          </p>
        </div>
        <Switch
          id="requireAuthentication"
          checked={requireAuthentication}
          onCheckedChange={handleRequireAuthenticationChange}
          disabled={isSavingAuth || hasPrice}
        />
      </div>
      {showAuthenticationTypes ? (
        <div className="space-y-3 rounded-lg border p-4">
          <div className="space-y-1">
            <Label>Allowed authentication types</Label>
            <p className="text-sm text-muted-foreground">
              Choose which sign-in methods viewers can use to access this meeting.
            </p>
          </div>
          <div className="grid gap-3">
            {AUTHENTICATION_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                htmlFor={`auth-type-${option.value}`}
                className="flex items-center gap-3 text-sm"
              >
                <Checkbox
                  id={`auth-type-${option.value}`}
                  checked={allowedAuthenticationTypes.includes(option.value)}
                  disabled={isSavingAuthTypes}
                  onCheckedChange={(checked) =>
                    void handleAllowedAuthenticationTypesChange(
                      option.value,
                      checked === true,
                    )
                  }
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
      {showAuthenticationTypes ? (
        <div className="space-y-2 rounded-lg border p-4">
          <div className="space-y-1">
            <Label htmlFor="authenticationMessage">Sign-in message</Label>
            <p className="text-sm text-muted-foreground">
              Optional custom message on the meeting sign-in page. Leave blank to use the
              default prompt.
            </p>
          </div>
          <Textarea
            id="authenticationMessage"
            value={authenticationMessage}
            disabled={isSavingAuthMessage}
            rows={3}
            placeholder="Enter the phone number or email you used to purchase to access this meeting"
            onChange={(event) => setAuthenticationMessage(event.target.value)}
            onBlur={(event) => void handleAuthenticationMessageSave(event.target.value)}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <Label>Access products</Label>
        <p className="text-sm text-muted-foreground">
          Purchases of these products also grant access when signing in to this meeting.
        </p>
        <ProductCatalogMultiPicker
          value={accessProductIds}
          products={catalogProducts}
          excludeProductId={productId}
          disabled={isSaving}
          onChange={(next) => void handleAccessChange(next)}
        />
      </div>
    </div>
  );
}
